/**
 * 对齐 isp_pro/dataReport 的曝光 / 点击上报实现。
 * 与国家站既有 dataPort/index.ts 中的 legacy 方法并存，不修改 legacy 行为。
 */
import ja from './ja';
import { getRouteMetaInfo } from '@/router/routeMeta';

type ParamObject = Record<string, any>;

const getStandardParamStr = (paramObj: ParamObject): string => {
  const params: string[] = [];
  for (const key in paramObj) {
    params.push(`${key}=${paramObj[key] ?? 'NULL'}`);
  }
  return params.join(',');
};

const getStandardExpParam = (eventParam: ParamObject, ext: ParamObject): ParamObject => {
  const result: ParamObject = {};
  result.event_param = eventParam;
  if (ext) {
    for (const k in ext) {
      result[k] = decodeURIComponent(ext[k]);
    }
  }
  return result;
};

const getTimeoutPromise = (timeout = 200): Promise<unknown> => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve(undefined);
    }, timeout);
  });
};

const serializeEventParam = (eventParam: ParamObject) => {
  const payload = { ...eventParam };
  if (payload.needJSON) {
    delete payload.needJSON;
    return JSON.stringify(payload);
  }
  return getStandardParamStr(payload);
};

/**
 * 曝光上报（对齐 isp_pro expReport → window.expLogJSON）
 */
const standardExpReport = (
  pageId: string,
  eventId: string,
  eventParam?: ParamObject,
  ext?: ParamObject,
): Promise<unknown> => {
  const actionPromise: Promise<unknown> = new Promise((resolve, reject) => {
    ja.getExpLogJSON().then(expLogJSON => {
      if (!expLogJSON) {
        resolve(undefined);
        return;
      }
      try {
        if (eventParam) {
          const paramStr = serializeEventParam(eventParam);
          if (ext) {
            expLogJSON(pageId, eventId, getStandardExpParam(paramStr, ext));
          } else {
            expLogJSON(pageId, eventId, paramStr);
          }
        } else {
          expLogJSON(pageId, eventId);
        }
        resolve(undefined);
      } catch (error) {
        console.error(`[pageId:${pageId}, eventId:${eventId}] 标准曝光上报失败`, error);
        reject(error);
      }
    });
  });

  return Promise.race([actionPromise, getTimeoutPromise()]);
};

/**
 * 曝光上报：自动取当前路由 meta.pageId（对齐 customClickReportSimple 的 pageId 解析方式）
 */
const expReportSimple = (...trackInfo: [string, Record<string, any>?]) => {
  const currentPath = window.location.pathname;
  const meta = getRouteMetaInfo(currentPath);
  if (meta?.pageId && trackInfo?.length) {
    const [eventId, eventParam] = trackInfo;
    void standardExpReport(meta.pageId, eventId, eventParam);
  }
};

/**
 * 点击上报（对齐 isp_pro customClickReport → window.log）
 */
const standardClickReport = (
  pageId: string,
  eventId: string,
  eventParam?: ParamObject,
  ext?: ParamObject,
): Promise<unknown> => {
  const actionPromise: Promise<unknown> = new Promise((resolve, reject) => {
    ja.getLog().then(log => {
      if (!log) {
        resolve(undefined);
        return;
      }
      try {
        if (eventParam) {
          const paramStr = serializeEventParam(eventParam);
          if (ext) {
            log(pageId, eventId, getStandardExpParam(paramStr, ext));
          } else {
            log(pageId, eventId, paramStr);
          }
        } else {
          log(pageId, eventId);
        }
        resolve(undefined);
      } catch (error) {
        console.error(`[pageId:${pageId}, eventId:${eventId}] 标准点击上报失败`, error);
        reject(error);
      }
    });
  });

  return Promise.race([actionPromise, getTimeoutPromise()]);
};

export {
  standardExpReport,
  expReportSimple,
  standardClickReport,
};
